import { useEffect, useRef, useState } from 'react';
import * as THREE from 'three';
import { createCoreboundRenderer } from './createCoreboundRenderer';
import { observeElementSize } from '../../utils/observeElementSize';
import { createTravelDie } from './portalTravel';
import DieMiniature from './DieMiniature';
import { getDie } from './dice';
import galaxyBackdrop from './coreboundGalaxyBackdrop.svg';
import { createVictoryGalaxy } from './victoryGalaxy';

const STARS = Array.from({ length: 40 }, (_, index) => ({
  x: 3 + ((index * 37 + 11) % 94),
  y: 7 + ((index * 23 + 17) % 82),
  size: index % 7 === 0 ? 3 : 1.5,
}));

const VictoryChampion = ({ dieId, faceValues, active, reduceMotion }) => {
  const hostRef = useRef(null);
  const [webglAvailable, setWebglAvailable] = useState(false);
  const skin = getDie(dieId);

  useEffect(() => {
    const host = hostRef.current;
    if (!active || !host) return undefined;
    let renderer;
    try {
      renderer = createCoreboundRenderer({ antialias: true, alpha: true });
    } catch {
      setWebglAvailable(false);
      return undefined;
    }
    renderer.outputColorSpace = THREE.SRGBColorSpace;
    renderer.toneMapping = THREE.ACESFilmicToneMapping;
    renderer.toneMappingExposure = 1.25;
    renderer.setClearColor(0x000000, 0);
    host.appendChild(renderer.domElement);
    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(36, 1, 0.1, 30);
    camera.position.set(0, 0.15, 6.3);
    camera.lookAt(0, 0.15, 0);
    const galaxy = createVictoryGalaxy({ dieColor: skin.color, reduceMotion });
    scene.add(galaxy.mesh);
    scene.add(new THREE.HemisphereLight(0xe7faff, 0x191131, 2.4));
    const key = new THREE.DirectionalLight(0xffedb9, 5);
    key.position.set(-3, 4, 5);
    scene.add(key);
    const rim = new THREE.PointLight(0xa9f8ff, 8, 12);
    rim.position.set(3, 1, -1);
    scene.add(rim);
    const die = createTravelDie(dieId, faceValues);
    die.scale.setScalar(1.9);
    scene.add(die);
    let elapsed = 0;
    let lastTime = null;
    let animationFrame = null;
    let onScreen = true;
    let contextLost = false;

    const render = () => {
      galaxy.update(elapsed);
      const rise = reduceMotion ? 1 : 1 - Math.pow(1 - Math.min(1, elapsed / 1.4), 3);
      die.position.y = -0.04 - (1 - rise) * 0.7 + (reduceMotion ? 0 : Math.sin(elapsed * 1.35) * 0.1);
      die.rotation.set(-0.23, 0.48 + (reduceMotion ? 0 : elapsed * 0.18), -0.08);
      renderer.render(scene, camera);
    };
    const animate = (now) => {
      animationFrame = null;
      if (contextLost || !onScreen || document.hidden) return;
      if (lastTime !== null) elapsed += Math.min(0.06, (now - lastTime) / 1000);
      lastTime = now;
      render();
      if (!reduceMotion) animationFrame = requestAnimationFrame(animate);
    };
    const resume = () => {
      cancelAnimationFrame(animationFrame);
      animationFrame = null;
      lastTime = null;
      if (!contextLost && onScreen && !document.hidden) animationFrame = requestAnimationFrame(animate);
    };
    const onContextLost = (event) => {
      event.preventDefault();
      contextLost = true;
      cancelAnimationFrame(animationFrame);
      renderer.domElement.style.visibility = 'hidden';
      setWebglAvailable(false);
    };
    renderer.domElement.addEventListener('webglcontextlost', onContextLost);
    document.addEventListener('visibilitychange', resume);
    const stopObserving = observeElementSize(host, () => {
      const width = Math.max(1, host.clientWidth);
      const height = Math.max(1, host.clientHeight);
      renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 1.5, Math.sqrt(800000 / (width * height))));
      renderer.setSize(width, height, false);
      camera.aspect = width / height;
      camera.updateProjectionMatrix();
      galaxy.resize(camera.aspect);
      if (!contextLost) render();
    });
    const observer = typeof IntersectionObserver === 'function'
      ? new IntersectionObserver(([entry]) => {
        onScreen = entry.isIntersecting;
        resume();
      })
      : null;
    observer?.observe(host);
    setWebglAvailable(true);
    resume();

    return () => {
      cancelAnimationFrame(animationFrame);
      stopObserving();
      observer?.disconnect();
      document.removeEventListener('visibilitychange', resume);
      renderer.domElement.removeEventListener('webglcontextlost', onContextLost);
      const geometries = new Set();
      const materials = new Set();
      const textures = new Set();
      scene.traverse((object) => {
        if (object.geometry) geometries.add(object.geometry);
        if (object.material) {
          materials.add(object.material);
          if (object.material.map) textures.add(object.material.map);
        }
      });
      geometries.forEach((geometry) => geometry.dispose());
      materials.forEach((material) => material.dispose());
      textures.forEach((texture) => texture.dispose());
      renderer.dispose();
      renderer.forceContextLoss?.();
      renderer.domElement.remove();
    };
  }, [active, dieId, faceValues, reduceMotion, skin.color]);

  return (
    <div className="coreboundVictoryChampion" aria-hidden="true" style={{ '--champion-color': skin.color }}>
      {!webglAvailable && <img className="coreboundVictoryRecoveredGalaxy" src={galaxyBackdrop} alt="" />}
      <div className="coreboundVictoryAurora is-cyan" />
      <div className="coreboundVictoryAurora is-violet" />
      <div className="coreboundVictoryStars">
        {STARS.map((star, index) => (
          <i key={index} style={{
            left: `${star.x}%`, top: `${star.y}%`, width: star.size, height: star.size,
            '--star-delay': `${0.25 + Math.abs(star.x - 50) / 22 + Math.abs(star.y - 50) / 45}s`,
            '--twinkle-delay': `${index * -0.37}s`,
          }} />
        ))}
      </div>
      <div className="coreboundVictoryChampionBeam" />
      <div className="coreboundVictoryChampionHalo" />
      <div className="coreboundVictoryChampionOrbit" />
      <div className="coreboundVictoryChampionScene" ref={hostRef} />
      {!webglAvailable && <DieMiniature dieId={dieId} faceValues={faceValues} className="coreboundVictoryChampionFallback" />}
      <svg className="coreboundVictoryEnergyCrown" viewBox="0 0 100 70" fill="none">
        <path d="M14 47 9 18 31 31 50 7 69 31 91 18 86 47Z" pathLength="1" />
        <path d="M15 53H85M20 59H80" pathLength="1" />
        <circle cx="50" cy="7" r="3" /><circle cx="9" cy="18" r="2.5" /><circle cx="91" cy="18" r="2.5" />
      </svg>
      <div className="coreboundVictoryRetreatingDarkness" />
      <span className="coreboundVictoryChampionCaption">YOUR {skin.name.toUpperCase()} DIE · THE SIGNAL THAT SET US FREE</span>
    </div>
  );
};

export default VictoryChampion;
